import React, { useState, useEffect } from 'react';
import { 
  Form, 
  Input, 
  Select, 
  Button, 
  Card,
  Typography,
  Row,
  Col,
  Upload,
  Space,
  Modal,
  Table,
  Checkbox,
  Tag,
  Spin,
  message
} from 'antd';
import { 
  ArrowLeftOutlined,
  PictureOutlined,
  PlusOutlined,
  CloseOutlined,
  SearchOutlined,
  UploadOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate, useParams } from 'react-router-dom';
import AdminLayout from '../../../components/Layouts/AdminLayout/AdminLayout';
import UnitTable from '../../../components/admin/UnitTable';
import { adminTextbookApi, adminUnitApi, fileApi, adminTrophyBadgeApi } from '@/services/api';
import { message as antdMessage } from 'antd';

const { Title } = Typography;
const { Option } = Select;

interface TextbookDetail {
  id: string;
  type: string;
  name: string;
  thumbnailUrl?: string;
  registrant: string;
  registrationDate: string;
}

interface UnitData {
  id: string | number;
  // UnitTable 호환 필드들
  unitType?: string;
  unitCategory?: string;
  title?: string;
  code?: string;
  createdAt?: string;
  // 구버전 호환
  type?: string;
  name?: string;
  registrant?: string;
  registrationDate?: string;
}

const formatRegistrationDate = (value?: string | null): string => {
  if (!value) return '';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD HH:mm') : String(value);
};

const AdminTextbookDetail: React.FC = () => {
  const navigate = useNavigate();
  const { textbookId } = useParams<{ textbookId: string }>();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [textbookData, setTextbookData] = useState<TextbookDetail | null>(null);
  const [unitModalVisible, setUnitModalVisible] = useState(false);
  const [selectedUnits, setSelectedUnits] = useState<string[]>([]);
  const [registeredUnits, setRegisteredUnits] = useState<UnitData[]>([]);
  const [thumbnailPreview, setThumbnailPreview] = useState<string | undefined>(undefined);
  
  // 뱃지 선택 관련 state
  const [badgeModalVisible, setBadgeModalVisible] = useState(false);
  const [badgeList, setBadgeList] = useState<any[]>([]);
  const [selectedBadge, setSelectedBadge] = useState<any>(null);
  const [badgeLoading, setBadgeLoading] = useState(false);

  const isEditMode = !!textbookId;

  const typeOptions = [
    { value: '파닉스데이', label: '파닉스데이' },
    { value: '스타터_파닉스', label: '스타터 파닉스' },
    { value: '스타터_뉘앙스', label: '스타터 뉘앙스' },
    { value: '스타터_스트럭처', label: '스타터 스트럭처' },
    { value: '에세이', label: '에세이' },
    { value: '통번역', label: '통번역' },
    { value: '리딩북', label: '리딩북' }
  ];

  const handleBackToList = () => {
    navigate('/admin/textbook');
  };

  const [usageCount, setUsageCount] = useState(0);

  const handleSubmit = async (values: any) => {
    if (isEditMode && textbookId) {
      try {
        const res = await adminTextbookApi.hasLearningHistory(textbookId);
        const payload = res?.data || res;
        const usageCount = payload?.usageCount ?? payload?.data?.usageCount ?? 0;
        if (usageCount > 0) {
          Modal.confirm({
            title: '학습이력이 있는 교재입니다.',
            content: '이 교재는 학생들이 학습한 기록이 있습니다. 수정하시겠습니까?',
            okText: '수정하기',
            cancelText: '취소',
            centered: true,
            onOk: () => proceedSubmit(values),
          });
          return;
        }
      } catch (_) {}
    }
    await proceedSubmit(values);
  };

  const proceedSubmit = async (values: any) => {
    setSaving(true);
    try {
      // coverUrl은 http(s) URL만 허용, data URL 방지 및 255 초과 방지
      const candidateUrls = [values?.thumbnailUrl, thumbnailPreview].filter((u: any) => {
        return typeof u === 'string' && !String(u).startsWith('data:') && /^https?:\/\//.test(String(u));
      }) as string[];
      const resolvedCoverUrl = candidateUrls.length > 0 && candidateUrls[0].length <= 255 ? candidateUrls[0] : undefined;

      const payload = {
        title: values.name,
        coverUrl: resolvedCoverUrl,
        category: values.type,
        description: '',
        gradeLabel: '',
        badgeId: selectedBadge?.id || null
      };
      let createdId: string | number | undefined = textbookId;
      if (isEditMode && textbookId) {
        await adminTextbookApi.update(textbookId, payload);
        antdMessage.success('교재가 수정되었습니다.');
      } else {
        const res = await adminTextbookApi.create(payload);
        const data = res?.data || res;
        createdId = data?.id || data?.data?.id;
        antdMessage.success('교재가 등록되었습니다.');
      }

      // 유닛 연결 업데이트
      try {
        const linkId = createdId || textbookId;
        if (linkId) {
          const unitIds: number[] = Array.from(new Set(
            registeredUnits
              .map((u) => Number(u.id))
              .filter((n: number) => !Number.isNaN(n))
          ));
          await adminTextbookApi.linkUnits(String(linkId), { unitIds } as any);
          // 저장 직후 서버 상태 재조회로 UI 동기화
          try {
            const ures = await adminTextbookApi.listUnits(String(linkId));
            const udata = ures?.data || ures;
            const list = (udata?.data || udata || []) as any[];
            const rows = list.map((d) => ({
              id: d.id,
              unitType: d.unitType,
              unitCategory: d.unitCategory,
              title: d.title,
              code: d.code,
              createdAt: d.createdAt,
              registrant: d.registrant,
              registrationDate: d.registrationDate || d.createdAt,
            } as UnitData));
            setRegisteredUnits(rows);
          } catch (_) {}
        }
      } catch (_) {}

      // 수정 모드에서는 현재 페이지에서 상태만 동기화, 신규 등록은 상세로 이동
      if (!isEditMode && createdId) {
        navigate(`/admin/textbook/${createdId}`);
      }
    } catch (error) {
      antdMessage.error('교재 저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/textbook');
  };

  const handleImageUpload = async ({ file }: any) => {
    try {
      const res = await fileApi.upload(file, 'textbook');
      const url = res?.data?.url || res?.url || res; // 파일 업로드 응답 형식 호환
      if (url) {
        setThumbnailPreview(url);
        form.setFieldsValue({ thumbnailUrl: url });
        message.success('썸네일 업로드가 완료되었습니다.');
      } else {
        message.error('업로드 응답에 URL이 없습니다.');
      }
    } catch (_) {
      message.error('썸네일 업로드에 실패했습니다.');
    }
  };

  const removeImage = () => {
    setThumbnailPreview(undefined);
    form.setFieldsValue({ thumbnailUrl: undefined });
  };

  const handleUnitRegistration = () => {
    setUnitModalVisible(true);
  };

  // 뱃지 선택 모달 열기
  const handleBadgeSelection = async () => {
    setBadgeLoading(true);
    try {
      const res = await adminTrophyBadgeApi.listMaster({ type: 'badge' });
      const data = res?.data || res;
      setBadgeList(data?.content || []);
      setBadgeModalVisible(true);
    } catch (e) {
      antdMessage.error('뱃지 목록을 불러오지 못했습니다.');
    } finally {
      setBadgeLoading(false);
    }
  };

  // 뱃지 선택
  const handleBadgeSelect = (badge: any) => {
    setSelectedBadge(badge);
    setBadgeModalVisible(false);
    antdMessage.success(`"${badge.name}" 뱃지가 선택되었습니다.`);
  };

  // 뱃지 선택 해제
  const handleBadgeRemove = () => {
    setSelectedBadge(null);
    antdMessage.success('뱃지 선택이 해제되었습니다.');
  };

  const handleUnitSelect = (selectedUnitIds: string[]) => {
    setSelectedUnits(selectedUnitIds);
  };

  const handleRemoveUnit = (unitId: string) => {
    setRegisteredUnits(prev => prev.filter(unit => String(unit.id) !== String(unitId)));
    message.success('유닛이 제거되었습니다.');
  };

  const handleUnitModalOk = async () => {
    try {
      // 선택된 유닛 상세를 조회하여 정확한 필드로 매핑
      const details = await Promise.all(selectedUnits.map(async (id) => {
        const res = await adminUnitApi.detail(id);
        const raw = res?.data || res; // ApiResponse 또는 직접 데이터
        const d = raw?.data || raw;
        return {
          id: d?.id ?? id,
          unitType: d?.unitType,
          unitCategory: d?.unitCategory,
          title: d?.title,
          code: d?.code,
          createdAt: d?.createdAt,
          registrant: d?.registrant,
          registrationDate: d?.registrationDate || d?.createdAt,
        } as UnitData;
      }));

      // 중복 제거 후 신규 유닛을 상단에 배치
      setRegisteredUnits(prev => {
        const existingIds = new Set(prev.map(u => String(u.id)));
        const toAdd = details.filter(u => !existingIds.has(String(u.id)));
        if (toAdd.length === 0) {
          return prev;
        }
        return [...toAdd, ...prev];
      });

      setUnitModalVisible(false);
      setSelectedUnits([]);
      message.success(`${details.length}개의 유닛이 교재에 등록되었습니다.`);
    } catch (e) {
      message.error('유닛 정보를 불러오지 못했습니다.');
    }
  };

  const handleUnitModalCancel = () => {
    setUnitModalVisible(false);
    setSelectedUnits([]);
  };

  // 미리보기 URL 우선순위: 업로드 직후(preview state) > 폼 값 > 로드된 상세 데이터
  const previewUrl = thumbnailPreview || (form.getFieldValue('thumbnailUrl') as string | undefined) || textbookData?.thumbnailUrl;

  useEffect(() => {
    const fetchDetail = async () => {
      if (!isEditMode || !textbookId) {
        form.setFieldsValue({ type: '파닉스데이', registrant: '관리자' });
        return;
      }
      setLoading(true);
      try {
        // 기본 정보
        const res = await adminTextbookApi.detail(textbookId);
        const data = res?.data || res;
        const name = data?.name || data?.data?.name;
        const type = data?.type || data?.data?.type;
        const registrationDate = data?.registrationDate || data?.data?.registrationDate;
        const formattedRegistrationDate = formatRegistrationDate(registrationDate);
        const coverUrl = data?.coverUrl || data?.data?.coverUrl;
        const registrant = data?.registrant || data?.data?.registrant || '관리자';
        const usageCount = data?.usageCount || data?.data?.usageCount || 0;
        const badgeId = data?.badgeId || data?.data?.badgeId;
        
        setTextbookData({ id: textbookId, type, name, thumbnailUrl: coverUrl, registrant, registrationDate: formattedRegistrationDate } as TextbookDetail);
        setThumbnailPreview(coverUrl);
        form.setFieldsValue({ type, name, thumbnailUrl: coverUrl, registrant, registrationDate: formattedRegistrationDate });
        setUsageCount(usageCount);
        
        // 뱃지 정보 로드
        if (badgeId) {
          try {
            const badgeRes = await adminTrophyBadgeApi.getMasterDetail(badgeId);
            const badgeData = badgeRes?.data || badgeRes;
            setSelectedBadge(badgeData);
          } catch (e) {
            console.error('뱃지 정보 로드 실패:', e);
          }
        }

        // 연결된 유닛 목록
        const ures = await adminTextbookApi.listUnits(textbookId);
        const udata = ures?.data || ures;
        const list = (udata?.data || udata || []) as any[];
        const rows = list.map((d) => ({
          id: d.id,
          unitType: d.unitType,
          unitCategory: d.unitCategory,
          title: d.title,
          code: d.code,
          createdAt: d.createdAt,
          registrant: d.registrant,
          registrationDate: d.registrationDate || d.createdAt,
        } as UnitData));
        setRegisteredUnits(rows);
      } catch (e) {
        antdMessage.error('교재 정보를 불러오지 못했습니다.');
      } finally {
        setLoading(false);
      }
    };
    fetchDetail();
  }, [textbookId, form, isEditMode]);

  if (loading) {
    return (
      <AdminLayout>
        <div style={{ padding: '24px', textAlign: 'center', minHeight: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div>로딩 중...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div style={{ padding: '24px' }}>
        {/* 헤더 */}
        <div style={{ marginBottom: 24 }}>
          <Space style={{ marginBottom: 16 }}>
            <Button 
              icon={<ArrowLeftOutlined />} 
              onClick={handleBackToList}
            >
              목록으로
            </Button>
          </Space>
          <Title level={2}>
            {isEditMode ? '교재 상세' : '교재 등록'}
          </Title>
        </div>

        {/* 교재 정보 폼 */}
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
            <Col span={6}>
              <Form.Item
                label="유형"
                name="type"
                rules={[
                  { required: true, message: '유형을 선택해주세요' }
                ]}
              >
                <Select
                  placeholder="유형을 선택하세요"
                  size="large"
                >
                  {typeOptions.map(option => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="교재명"
                name="name"
                rules={[
                  { required: true, message: '교재명을 입력해주세요' }
                ]}
              >
                <Input
                  placeholder="교재명을 입력하세요"
                  size="large"
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label="교재 썸네일 이미지"
                name="thumbnailUrl"
              >
                <div className='flex flex-col gap-3'>
                  <span className='text-xs text-gray-500'>썸네일 이미지는 10:14 비율로 업로드해주세요.</span>
                  {previewUrl ? (
                    <>
                    <div className='relative aspect-[10/14]' style={{ width: '200px', aspectRatio: '10/14' }}>
                      <img 
                        src={previewUrl} 
                        alt="교재 썸네일" 
                        className='object-cover w-full h-full rounded-lg border border-gray-200'
                      />
                      <Button
                        type="primary"
                        shape="circle"
                        size="small"
                        icon={<CloseOutlined />}
                        style={{
                          position: 'absolute',
                          top: '-8px',
                          right: '-8px',
                          backgroundColor: '#ef4444',
                          borderColor: '#ef4444',
                          zIndex: 10
                        }}
                        onClick={removeImage}
                      />
                    </div>
                      <Upload
                        accept="image/*"
                        showUploadList={false}
                        customRequest={({ file, onSuccess }) => {
                          (async () => {
                            await handleImageUpload({ file });
                            onSuccess && onSuccess('ok');
                          })();
                        }}
                      >
                        <Button icon={<UploadOutlined />}>
                          썸네일 교체
                        </Button>
                      </Upload>
                    </>
                  ) : (
                    <Upload
                      accept="image/*"
                      showUploadList={false}
                      customRequest={({ file, onSuccess }) => {
                        (async () => {
                          await handleImageUpload({ file });
                          onSuccess && onSuccess('ok');
                        })();
                      }}
                      
                    >
                      <div className='flex flex-col justify-center items-center rounded-lg border border-gray-200 aspect-[10/14]' style={{ width: '200px', aspectRatio: '10/14' }}>
                        <PictureOutlined className='text-2xl text-gray-400' />
                        <div className='mt-2 text-gray-400'>썸네일 업로드</div>
                      </div>
                    </Upload>
                  )}
                </div>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="유닛 등록"
              >
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={handleUnitRegistration}
                  size="large"
                  style={{ width: '100%', height: '150px' }}
                >
                  유닛 등록
                </Button>
              </Form.Item>
            </Col>

            {/* 완료 뱃지 선택 */}
            <Col span={24}>
              <Form.Item
                label="완료 시 수여 뱃지"
                extra="학생이 이 교재의 모든 유닛을 완료하면 자동으로 부여될 뱃지를 선택하세요 (선택사항)"
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  {selectedBadge ? (
                    <Card size="small" style={{ width: '300px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        {selectedBadge.iconUrl && (
                          <img 
                            src={selectedBadge.iconUrl} 
                            alt={selectedBadge.name}
                            style={{ width: '40px', height: '40px', objectFit: 'contain' }}
                          />
                        )}
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 'bold' }}>{selectedBadge.name}</div>
                          <div style={{ fontSize: '12px', color: '#666' }}>{selectedBadge.description}</div>
                        </div>
                        <Button 
                          type="text" 
                          danger 
                          icon={<CloseOutlined />} 
                          onClick={handleBadgeRemove}
                        />
                      </div>
                    </Card>
                  ) : (
                    <Button
                      type="dashed"
                      icon={<PlusOutlined />}
                      onClick={handleBadgeSelection}
                      loading={badgeLoading}
                      size="large"
                    >
                      뱃지 선택
                    </Button>
                  )}
                </div>
              </Form.Item>
            </Col>

        {/* 등록된 유닛 목록 */}
          {registeredUnits.length > 0 && (
            <div style={{ marginBottom: '32px' }}>
              <Title level={4} style={{ marginBottom: '16px' }}>등록된 유닛 목록</Title>
              <UnitTable
                showCheckbox={false}
                showSearch={false}
                size="small"
                pagination={false}
                dataSource={registeredUnits}
                showDeleteButton={true}
                onDelete={handleRemoveUnit}
              />
            </div>
          )}

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label="등록자"
                name="registrant"
              >
                <Input
                  disabled
                  size="large"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="등록일"
                name="registrationDate"
              >
                <Input
                  disabled
                  size="large"
                />
              </Form.Item>
            </Col>
          </Row>

          {/* 버튼 영역 */}
          <div style={{ textAlign: 'right', marginTop: 32 }}>
            <Space size="middle">
              <Button 
                onClick={handleCancel}
                size="large"
                style={{ height: 48, fontSize: 16, padding: '0 32px' }}
              >
                취소
              </Button>
              <Button 
                type="primary" 
                htmlType="submit"
                loading={saving}
                size="large"
                style={{ height: 48, fontSize: 16, padding: '0 32px' }}
              >
                {isEditMode ? '수정' : '등록'}
              </Button>
            </Space>
          </div>
        </Form>

        {/* 유닛 등록 모달 */}
        <Modal
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>유닛 등록</span>
              <span style={{ fontSize: '14px', color: '#666' }}>
                ({selectedUnits.length}개 선택)
              </span>
            </div>
          }
          open={unitModalVisible}
          onOk={handleUnitModalOk}
          onCancel={handleUnitModalCancel}
          width={1000}
          okText="등록"
          cancelText="취소"
        >
          <div style={{ padding: '20px 0' }}>
            <UnitTable
              showCheckbox={true}
              showSearch={true}
              maxHeight="400px"
              size="small"
              pagination={{ pageSize: 5, showTotal: (t) => `총 ${t} 개` }}
              selectedUnits={selectedUnits}
              onUnitSelect={handleUnitSelect}
            />
          </div>
        </Modal>

        {/* 뱃지 선택 모달 */}
        <Modal
          title="완료 뱃지 선택"
          open={badgeModalVisible}
          onCancel={() => setBadgeModalVisible(false)}
          footer={null}
          width={800}
        >
          <div style={{ padding: '20px 0' }}>
            {badgeLoading ? (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <Spin />
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                {badgeList.map((badge) => (
                  <Card
                    key={badge.id}
                    hoverable
                    onClick={() => handleBadgeSelect(badge)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      {badge.iconUrl && (
                        <img 
                          src={badge.iconUrl} 
                          alt={badge.name}
                          style={{ width: '50px', height: '50px', objectFit: 'contain' }}
                        />
                      )}
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                          {badge.name}
                          {badge.grade && (
                            <Tag color="blue" style={{ marginLeft: '8px' }}>
                              Grade {badge.grade}
                            </Tag>
                          )}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          {badge.description || '설명 없음'}
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
            {!badgeLoading && badgeList.length === 0 && (
              <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
                등록된 뱃지가 없습니다.
              </div>
            )}
          </div>
        </Modal>
      </div>
    </AdminLayout>
  );
};

export default AdminTextbookDetail; 